/**
 * Group Financial Features - matches backend GroupFinancialFeature enum
 * Used for progressive finance enablement in group creation and management.
 */
export const GroupFinancialFeature = {
  FUND_COLLECTION: 'FUND_COLLECTION',
  TONTINE_CYCLES: 'TONTINE_CYCLES',
  GROUP_SAVINGS: 'GROUP_SAVINGS',
  SAVE_TO_BUY: 'SAVE_TO_BUY',
  GROUP_BUY: 'GROUP_BUY',
  MARKETPLACE: 'MARKETPLACE',
  PAYMENT_LINKS: 'PAYMENT_LINKS',
  SUBSCRIPTIONS: 'SUBSCRIPTIONS',
  AD_MONETIZATION: 'AD_MONETIZATION',
};

/**
 * Feature metadata for UI display - labels and descriptions use i18n keys
 */
export const FINANCIAL_FEATURE_META = {
  [GroupFinancialFeature.FUND_COLLECTION]: {
    label: 'group.financialFeature.fundCollection',
    description: 'group.financialFeature.fundCollectionDesc',
    icon: 'giftcard_fill',
    prerequisites: ['group.financialFeature.prereq.payoutConfig', 'group.financialFeature.prereq.beneficiaryVerification'],
  },
  [GroupFinancialFeature.TONTINE_CYCLES]: {
    label: 'group.financialFeature.tontineCycles',
    description: 'group.financialFeature.tontineCyclesDesc',
    icon: 'arrow_2_circlepath',
    prerequisites: ['group.financialFeature.prereq.fundCollection', 'group.financialFeature.prereq.payoutConfig', 'group.financialFeature.prereq.dualApproval'],
  },
  [GroupFinancialFeature.GROUP_SAVINGS]: {
    label: 'group.financialFeature.groupSavings',
    description: 'group.financialFeature.groupSavingsDesc',
    icon: 'money_dollar_circle_fill',
    prerequisites: ['group.financialFeature.prereq.fundCollection', 'group.financialFeature.prereq.payoutConfig'],
  },
  [GroupFinancialFeature.SAVE_TO_BUY]: {
    label: 'group.financialFeature.saveToBuy',
    description: 'group.financialFeature.saveToBuyDesc',
    icon: 'cart_fill',
    prerequisites: ['group.financialFeature.prereq.fundCollection', 'group.financialFeature.prereq.marketplaceLinked'],
  },
  [GroupFinancialFeature.GROUP_BUY]: {
    label: 'group.financialFeature.groupBuy',
    description: 'group.financialFeature.groupBuyDesc',
    icon: 'shippingbox_fill',
    prerequisites: ['group.financialFeature.prereq.marketplaceEnabled', 'group.financialFeature.prereq.payoutConfig'],
  },
  [GroupFinancialFeature.MARKETPLACE]: {
    label: 'group.financialFeature.marketplace',
    description: 'group.financialFeature.marketplaceDesc',
    icon: 'bag_fill',
    prerequisites: ['group.financialFeature.prereq.marketplaceMode', 'group.financialFeature.prereq.sellerVerification'],
  },
  [GroupFinancialFeature.PAYMENT_LINKS]: {
    label: 'group.financialFeature.paymentLinks',
    description: 'group.financialFeature.paymentLinksDesc',
    icon: 'link',
    prerequisites: ['group.financialFeature.prereq.fundCollection', 'group.financialFeature.prereq.payoutConfig'],
  },
  [GroupFinancialFeature.SUBSCRIPTIONS]: {
    label: 'group.financialFeature.subscriptions',
    description: 'group.financialFeature.subscriptionsDesc',
    icon: 'arrow_2_circlepath',
    prerequisites: ['group.financialFeature.prereq.fundCollection', 'group.financialFeature.prereq.payoutConfig'],
  },
  [GroupFinancialFeature.AD_MONETIZATION]: {
    label: 'group.financialFeature.adMonetization',
    description: 'group.financialFeature.adMonetizationDesc',
    icon: 'megaphone_fill',
    prerequisites: ['group.financialFeature.prereq.adActivation', 'group.financialFeature.prereq.groupVerification'],
  },
};

/**
 * Feature dependencies - which features require others to be enabled first
 */
export const FEATURE_DEPENDENCIES = {
  [GroupFinancialFeature.TONTINE_CYCLES]: [GroupFinancialFeature.FUND_COLLECTION],
  [GroupFinancialFeature.GROUP_SAVINGS]: [GroupFinancialFeature.FUND_COLLECTION],
  [GroupFinancialFeature.SAVE_TO_BUY]: [GroupFinancialFeature.FUND_COLLECTION, GroupFinancialFeature.MARKETPLACE],
  [GroupFinancialFeature.GROUP_BUY]: [GroupFinancialFeature.MARKETPLACE],
  [GroupFinancialFeature.PAYMENT_LINKS]: [GroupFinancialFeature.FUND_COLLECTION],
  [GroupFinancialFeature.SUBSCRIPTIONS]: [GroupFinancialFeature.FUND_COLLECTION],
};

/**
 * Get all features as array for iteration
 */
export const ALL_FINANCIAL_FEATURES = Object.values(GroupFinancialFeature);